import React, { Component, createContext } from 'react';
import { connect } from 'react-redux';
import { Router, Switch } from 'react-router-dom';

import { get, isEmpty, isEqual } from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import {
  appName,
  pwaFeatureEnabled,
  pwaInstallUserChoices,
  storageTypes,
  storageKeys,
  storeExpiryEnabled,
  cashCardFeatureEnabled,
  restaurantDataDepthParams,
  restaurantPremiumFeaturesKeys,
  cashCardAPIParams,
  upsellFeatureEnabled,
  subscriptionModuleEnabled,
  isScreenOrientationPortrait,
  couponMaskedRoutes,
  feedbackMaskedRoutes,
  apiResponseCodes,
  engageSpecificRoutes,
  serverErrorCodes,
} from '../constants/globals';

import rootStore from '../store/store';
import history from '../helpers/history';
import {
  extractWhitelabelSubdomain,
  getDeviceType,
  getURLOrigin,
  isAuth401Error,
  isIOS,
  isMobilePlatform,
  isNativeWebView,
  notifyNativeSessionExpired,
  getAppInStartupPhase,
  setAppInStartupPhase,
  setIsApiRequestOnHold,
} from '../utils/helpers';
import { setItem, getItem } from '../utils/storage';
import { runWhenPersistorBootstrapped } from '../utils/activateInactivityReset';
import asyncRoute from '../helpers/hoc/asyncRoute';
import routes from '../routes';
import rewardRoutes from '../routes/rewards';
import orderingRoutes from '../routes/ordering';
import {
  fetchAppStartupInfoAction,
  dispatchContextualHome,
  dispatchSurface,
  dispatchApplicableBrandModulesAction,
  clearApiErrorResponseAction,
  hydrateAppStartupFromBootAction,
} from '../actions/common';
import {
  getBootModules,
  isValidFudrBoot,
  readFudrBoot,
  resolveSurface,
} from '../utils/fudrBoot';
import {
  markPersistorForPurgeAction,
  dispatchUserConsentAction,
  fetchMasterDataAction,
  fetchRestaurantDataAction,
  fetchGuestCashCardsAction,
  clearProgressiveWebAppDataAction,
} from '../actions/app.action';

import { dispatchLogoutAction, getGuestByIdAction } from '../actions/authentication';

import Spinner from '../components/Spinner';
import exceptionEmoji from '../assets/images/exception-emoji.svg';
import cancelIcon from '../assets/images/icons/close.svg';
import landscapeImage from '../assets/images/landscape.svg';

import DefaultPage from './Default';

import { Greeting } from '../components/SplashScreen/Greeting';
import './index.scss';
import pagesRoutes from '../routes/pages';
import { FUDR_SURFACES } from '../utils/fudrBoot';
import { renderRoutesForSurface } from './routing/surfaceRouteSets';
import { nativeEventType } from './Rewards/config';

const DesktopLandingScreen = asyncRoute(() => import('../components/DesktopLandingScreen'));
const SessionExpiredPopover = asyncRoute(() => import('../components/SessionExpiredPopover'));
const Scan = asyncRoute(() => import('./Scan'));
const RewardsScan = asyncRoute(() => import('./Rewards/Scan'));
const SiteNotFound = asyncRoute(() => import('./Site/components/NotFound'));
const Restaurant = asyncRoute(() => import('./Restaurant'));
const Cart = asyncRoute(() => import('./Cart'));
const Confirmation = asyncRoute(() => import('./Confirmation'));
const Payment = asyncRoute(() => import('./PaymentV1'));
const WelcomeScreen = asyncRoute(() => import('./WelcomeScreen'));
const OrderList = asyncRoute(() => import('./OrderList'));
const Account = asyncRoute(() => import('./Account'));
const Offers = asyncRoute(() => import('./Offers'));
const CustomLinkRedirect = asyncRoute(() => import('./CustomLinkRedirect'));
const AboutFudr = asyncRoute(() => import('./AboutFudr'));
const AboutOutlet = asyncRoute(() => import('./AboutOutlet'));
const TermsAndConditions = asyncRoute(() => import('./TermsAndConditions'));
const PrivacyPolicy = asyncRoute(() => import('./PrivacyPolicy'));
const HelpAndSupport = asyncRoute(() => import('./HelpAndSupport'));
const CashCards = asyncRoute(() => import('./CashCards'));
const DownloadApp = asyncRoute(() => import('./DownloadApp'));
const Deals = asyncRoute(() => import('./Deals'));
const CouponDetail = asyncRoute(() => import('./CouponDetail'));
const DealDetail = asyncRoute(() => import('./DealDetail'));
const NonExistentSubdomainFallback = asyncRoute(() =>
  import('./Fallback/NonExistentSubdomainFallback'),
);
const Feedback = asyncRoute(() => import('../containers/Feedback'));
const RewardsClubSignup = asyncRoute(() => import('./RewardsClubSignup'));
const Rewards = asyncRoute(() => import('./Rewards'));
const Wallet = asyncRoute(() => import('./Rewards/Wallet'));
const Ordering = asyncRoute(() => import('./Ordering'));
const Pages = asyncRoute(() => import('./Pages'));
const URLRedirectHandler = asyncRoute(() => import('./URLRedirectHandler'));
const View = asyncRoute(() => import('./View'));
const Site = asyncRoute(() => import('./Site'));

const deviceType = getDeviceType();
export const DeviceContext = createContext('');
export const SubscriptionContext = createContext({});

const engageRoutes = [
  routes.containers.deals,
  routes.containers.deal,
  routes.containers.coupon,
  routes.containers.feedback,
  routes.containers.rewardsClubSignup,
];

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isRestaurantEntityGroup: null, // If webapp is visited through a whitelabel subdomain, this state indicates whether the corresponding restaurant entity is a group or not
      isPWAInstallable: false, // To monitor whether the PWA ready to install
      deferredPrompt: null, // Stores the PWA app install event to be triggered later
      pwaInstallUserChoice: '', // Stores string indicating whether user accepted to install the PWA or dismissed ['accepted' | 'dismissed']
      enablePWAInstallWidgets: false, // Controls display of PWA install widgets - InstallAppetizer popup, card and button
      isPWAInstalled: false, // boolean to indicate whether the PWA app is installed; not supported on Apple devices
      contextualHome: '', // Represents the home route in context to the visited domain
      currentRoute: null, //will help in storing the current route
      showDesktopLandingScreen: false,
      depthParam: restaurantDataDepthParams.config,
      isScreenOrientationPortrait,
      greetingSplashActive: true, // Show greeting splash screen upon QR scan once on app visit
      showNonExistentSubdomainFallback: false,
      showSessionExpired: false,
      locationPathname: window.location.pathname,
    };

    if (window.screen && window.screen.orientation)
      window.screen.orientation.onchange = () => {
        this.setIsScreenOrientationPortrait(!this.state.isScreenOrientationPortrait);
      };
  }

  setIsScreenOrientationPortrait = (flag) => {
    this.setState({ isScreenOrientationPortrait: flag });
  };

  componentWillMount() {
    // logic to checked whether the URL contains hash or not
    const { href, hash, pathname } = window.location;
    if (hash.includes('#')) {
      let updatedUrl = href.replace('/#', '');
      window.location.href = updatedUrl;
    }
    window.onload = () => {
      setAppInStartupPhase(false);
    };
    //handle /rewards to /app redirection
    const paths = (pathname && pathname.split('/')) || [];
    if (paths.includes('rewards')) {
      const updatedPathName = paths
        .map((path) => (isEqual(path, 'rewards') ? rewardRoutes.rewards : path))
        .join('/');
      window.location.pathname = updatedPathName;
    }

    //handle /view to /app/view redirection

    // Commenting this for now to revert the IG feature back to /view/* routes.
    // if (paths.includes('view') && paths[1] === 'view') {
    //   const updatedPathName = paths
    //     .map((path, index) => {
    //       if (index === 1 && path === 'view') {
    //         return `${rewardRoutes.rewards}/view`;
    //       }
    //       return path;
    //     })
    //     .join('/');
    //   window.location.href = window.location.origin + updatedPathName + window.location.search;
    // }
  }

  componentDidMount() {
    const lodableElement = document.getElementById('loading-background');
    if (lodableElement) {
      lodableElement.style.display = 'none';
    }
    // FUDR-14656: Post only after the shell is hidden (or absent); the native app ignores appBootFailed from here on.
    if (window.ReactNativeWebView) {
      window.__fudrAppReady = true;
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ eventType: { type: nativeEventType.appReady } }),
      );
    }
    this.unlistenHistory = history.listen((location) => {
      const pathname = get(location, 'pathname', window.location.pathname);
      this.setState({ locationPathname: pathname });
      this.syncSurface(pathname);
    });
    this.syncSurface(window.location.pathname);
    const guestLoginData = getItem(storageTypes.localStorage, 'GuestLoginData');
    const guestId = get(guestLoginData, 'id', '');
    const { masterData } = this.props;
    //set currentTimeStamp in session storage
    setItem(storageTypes.sessionStorage, 'currentTimeStamp', moment().format());

    if (!isEqual(window.location.pathname, `/${routes.containers.payment}`)) {
      // do not get masterData and guestData if app is reloaded/redirected to payment from payment gateway
      // fetch master data
      if (isEmpty(masterData)) {
        this.props.fetchMasterDataAction({ appName });
      }

      if (!isEmpty(guestId)) {
        // rehydrate session with updated guest Data
        this.props.getGuestByIdAction({ guestId });
      }

      // Fetch guest cash cards to know whether the guest has availed cash card(s) from any outlet, only on scan screen to handle the edge case of showing My Cards.
      const currentPathName = window.location.pathname;
      currentPathName === `/${routes.containers.scan}` &&
        guestId &&
        this.getGuestCashCards(guestId);
    } else {
      // In case the payment portal opens fudr webapp ina new tab, loginData from redux store will be empty bu the guest id from localstorage will be available. We'll simply fetch the guest data at this point.
      if (isEmpty(this.props.loginData) && !isEmpty(guestId)) {
        // rehydrate session with updated guest Data
        this.props.getGuestByIdAction({ guestId });
      }
    }

    //set external origin in session storage
    const originRoute = getURLOrigin();
    this.setState({ currentRoute: originRoute });
    setItem(storageTypes.sessionStorage, storageKeys.originRoute, originRoute);

    // Redirect all whitelabel subdomain URL hits to corresponding restaurant/restaurant-group's welcome screen
    const subDomain = extractWhitelabelSubdomain();
    if (subDomain) {
      let { host, protocol, pathname } = window.location;
      pathname = pathname.split('/');
      const updatedPathName = pathname[1];
      // resolve pathname

      switch (updatedPathName) {
        case couponMaskedRoutes.coupon:
          window.location.replace(
            `${protocol}//${host}/${routes.containers.coupon}/${pathname[2]}`,
          );
          break;
        case couponMaskedRoutes.deal:
          window.location.replace(`${protocol}//${host}/${routes.containers.deal}/${pathname[2]}`);
          break;
        case feedbackMaskedRoutes.feedback:
          window.location.replace(
            `${protocol}//${host}/${routes.containers.feedback}/${pathname[2] || ''}`,
          );
          break;
        default:
          break;
      }
      // Always hydrate modules + surface on whitelabel (incl. engage paths) so
      // exclusive Switch is not stuck on !surface Spinner (FUDR-14577).
      //
      // Wait for redux-persist REHYDRATE first, then apply __FUDR_BOOT__. Otherwise
      // session-persisted common.resources / themeData overwrite boot on same-tab
      // refresh (new tabs looked fresh; same tab looked stuck on old config).
      const { persistor } = rootStore;
      runWhenPersistorBootstrapped(persistor, () => {
        const fudrBoot = readFudrBoot();
        if (isValidFudrBoot(fudrBoot)) {
          // FUDR-14576: prodServer already fetched shell appStartup — hydrate and skip
          // client Call A. React Router uses contextualHome / site deep links.
          const appStartupInfo = get(fudrBoot, 'appStartupInfo', {});
          const applicableModules = getBootModules(fudrBoot);
          this.props.hydrateAppStartupFromBoot(appStartupInfo);
          this.props.dispatchApplicableBrandModules(applicableModules);
          this.applyWhitelabelContextualHome({
            updatedPathName,
            applicableModules,
            defaultScanCode: get(appStartupInfo, 'subdomainObj.path', ''),
            allowFullPageReplace: false,
          });
        } else {
          // fetch app startup data on sub domain resolution (CRA / missing boot)
          const appStartupReqBody = {
            subdomain: subDomain,
            findModules: true,
          };
          this.props.fetchAppStartupInfo({ appStartupReqBody }).then((response) => {
            if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
              this.props.dispatchApplicableBrandModules(get(response, 'data.modules', []));
              const applicableModules = get(response, 'data.modules', []);
              const defaultScanCode = get(response, 'data.subdomainObj.path', '');
              this.applyWhitelabelContextualHome({
                updatedPathName,
                applicableModules,
                defaultScanCode,
                allowFullPageReplace: true,
              });
            } else {
              // Unknown/invalid whitelabel subdomain — always show fallback.
              // Do not wait for Rewards Call B: on Call A failure modules/surface
              // never resolve, so /app would spin forever (Rewards never mounts).
              this.setState({
                showNonExistentSubdomainFallback: true,
              });
            }
          });
        }
      });

      if (engageSpecificRoutes.includes(updatedPathName)) {
        if (isNativeWebView) {
          window.location.replace(window.location.origin + `/${rewardRoutes.rewards}`);
        }
        // For Apple devices, simply enable the PWA widgets, if the whitelabel info exists
        this.enablePWAInstallWidgetsOnIOS();
      }
    } else {
      // In case whitelabel subdomain is not valid — legacy surface (no path promotion).
      this.props.dispatchSurface(
        resolveSurface({
          pathname: window.location.pathname,
          hasWhitelabelSubdomain: false,
        }),
      );
      // check for the scan route and deviceType
      if (
        (isEqual(window.location.pathname, `/${routes.containers.scan}`) ||
          isEqual(window.location.pathname, '/')) &&
        isEqual(deviceType, 'desktop')
      ) {
        this.setState({
          showDesktopLandingScreen: true,
        });
      }
      this.props.dispatchContextualHome(`/${routes.containers.scan}`);
    }

    this.enablePWAWidgetsForDiscovery();

    // Detect if the client supports the Add to home screen (PWA) feature and stash the event to be triggered later. Not supported on Apple devices.
    window.addEventListener('beforeinstallprompt', (event) => {
      // Prevent the mini-infobar from appearing by default
      event.preventDefault();

      // If it's a whitelabel visit, set true, otherwise check if the restaurantAppLink exists
      const enablePWAInstallWidgets = subDomain ? true : !!this.props.restaurantAppLink;

      // Stash the event so it can be triggered later and place PWA install widgets into DOM
      this.setState((prevState) => ({
        deferredPrompt: event,
        ...(!prevState.isPWAInstalled && { enablePWAInstallWidgets }),
        isPWAInstallable: true,
      }));
    });

    // Listen for the appinstalled event to track if the PWA was successfully installed. Not supported on Apple devices.
    window.addEventListener('appinstalled', (event) => {
      this.setPWAInstalledState(true);
      this.hidePWAInstallWidgets();
    });

    this.checkIsPWAAlreadyInstalled();
  }

  componentWillReceiveProps(nextProps) {
    // If session is marked for a clear, then clear the session and redirect to home
    if (
      !!storeExpiryEnabled &&
      !isEqual(
        get(nextProps, 'persistorMarkedForPurge'),
        get(this.props, 'persistorMarkedForPurge'),
      ) &&
      get(nextProps, 'persistorMarkedForPurge') === true
    ) {
      this.clearSessionAndRedirect();
    }

    //will store previous route of current route in window.history.state
    if (this.state.currentRoute) {
      const { origin, pathname } = window.location;
      const URL = `${origin}${pathname}`;
      if (!isEqual(get(this.state, 'currentRoute'), URL)) {
        setItem(
          storageTypes.sessionStorage,
          storageKeys.originRoute,
          get(this.state, 'currentRoute'),
        );
        this.setState({ currentRoute: URL });
      }
    }
  }

  componentDidUpdate(prevProps) {
    const currentLoginData = this.props.loginData;
    const previousLoginData = prevProps.loginData;

    if (!isEmpty(currentLoginData) && currentLoginData !== previousLoginData) {
      const guestId = get(currentLoginData, 'id', '');

      const currentPathName = window.location.pathname;

      // Fetch guest cash cards to know whether the guest has availed cash card(s) from any outlet, only on scan screen to handle the edge case of showing My Cards.
      if (currentPathName === `/${routes.containers.scan}`) {
        guestId && this.getGuestCashCards(guestId);
      }
    }

    const currentRestaurantAppLink = this.props.restaurantAppLink;
    const previousRestaurantAppLink = prevProps.restaurantAppLink;

    if (currentRestaurantAppLink !== previousRestaurantAppLink) {
      this.enablePWAWidgetsForDiscovery();
    }

    const errorResponse = get(this, 'props.errorResponse');
    const prevErrorResponse = get(prevProps, 'errorResponse');
    const subDomain = extractWhitelabelSubdomain();
    if (
      !subDomain &&
      errorResponse !== prevErrorResponse &&
      !isEmpty(errorResponse) &&
      isAuth401Error(errorResponse)
    ) {
      this.handleSessionExpiredErrorResponse(errorResponse);
    }

    if (!isEqual(prevProps.modules, this.props.modules)) {
      this.syncSurface(window.location.pathname, this.props.modules);
    }
  }

  handleContextualRedirection = () => { };

  renderGreetingSplashScreenComponent = (container) => {
    const { guestData, restaurantGroup, restaurant, appStartupInfo } = this.props;
    const { greetingSplashActive } = this.state;

    const guestName = get(guestData, 'name', '');
    const restaurantEntityName = !isEmpty(restaurantGroup)
      ? get(restaurantGroup, 'name')
      : get(restaurant, 'name')
        ? get(restaurant, 'name')
        : get(appStartupInfo, 'title');

    // Guest-visited restaurant related variables
    const guestVisitedRestaurants = get(guestData, 'visitedRestaurants', {}); // Restaurants where the guest has previously ordered
    const visitedRestaurantCode = get(restaurant, 'restaurantCode', '');
    const hasGuestVisitedRestaurantBefore = this.hasGuestVisitedRestaurantBefore(
      guestVisitedRestaurants,
      visitedRestaurantCode,
    );
    return (
      <Greeting
        active={greetingSplashActive}
        deactivate={() => this.setGreetingSplashActive(false)}
        loggedInGuestName={guestName}
        revisit={hasGuestVisitedRestaurantBefore}
        outletName={restaurantEntityName}
        showTimeSalutation
        container={container}
      />
    );
  };

  hasGuestVisitedRestaurantBefore = (guestVisitedRestaurants = [], visitedRestaurantCode = '') =>
    !isEmpty(guestVisitedRestaurants) && guestVisitedRestaurants.includes(visitedRestaurantCode);

  setGreetingSplashActive = (flag) => {
    this.setState({ greetingSplashActive: flag });
  };

  // Check if the app is already installed on the device, and update the isPWAInstalled state accordingly
  checkIsPWAAlreadyInstalled = async () => {
    if (!('getInstalledRelatedApps' in navigator)) return;

    const relatedApps = await navigator.getInstalledRelatedApps();

    if (!isEmpty(relatedApps)) {
      const appAlreadyInstalled = relatedApps.some((app) => app.platform === 'webapp');
      appAlreadyInstalled && this.setState({ isPWAInstalled: true });
    }
  };

  /**
   * Single live surface in Redux (`common.surface`) via resolveSurface.
   * No whitelabel subdomain → always legacy (path must not promote).
   * Whitelabel with empty modules → wait (avoid flash of legacy before site/app).
   */
  syncSurface = (pathname = window.location.pathname, modules = this.props.modules) => {
    const hasWhitelabelSubdomain = !!extractWhitelabelSubdomain();
    if (hasWhitelabelSubdomain && isEmpty(modules)) {
      return;
    }
    const nextSurface = resolveSurface({ pathname, modules, hasWhitelabelSubdomain });
    if (nextSurface !== this.props.surface) {
      this.props.dispatchSurface(nextSurface);
    }
  };

  /**
   * Set contextual home from modules. When allowFullPageReplace is true (CRA / no boot),
   * also window.location.replace to the home path (legacy double-nav behaviour).
   * When false (FUDR-14576 boot hydrate), React Router Redirect uses contextualHome only.
   */
  applyWhitelabelContextualHome = ({
    updatedPathName,
    applicableModules,
    defaultScanCode,
    allowFullPageReplace = true,
  }) => {
    if (isEmpty(applicableModules)) {
      return;
    }

    const pathname = window.location.pathname || `/${updatedPathName || ''}`;
    // applyWhitelabelContextualHome only runs on whitelabel hosts.
    const surface = resolveSurface({
      pathname,
      modules: applicableModules,
      hasWhitelabelSubdomain: true,
    });
    this.props.dispatchSurface(surface);

    let redirectToPathname = '';
    const isRewardProgramEnabled = get(
      applicableModules.find((module) => module.code === 'reward'),
      'isActive',
    );

    if (isEqual(updatedPathName, rewardRoutes.rewards)) {
      if (isRewardProgramEnabled) {
        this.props.dispatchContextualHome(`/${rewardRoutes.rewards}`);
        redirectToPathname = `/${rewardRoutes.rewards}`;
      } else {
        this.setState({ showNonExistentSubdomainFallback: true });
        return;
      }
    } else if (isEqual(updatedPathName, orderingRoutes.ordering)) {
      this.props.dispatchContextualHome(`/${orderingRoutes.ordering}`);
      redirectToPathname = `/${orderingRoutes.ordering}`;
    } else if (surface === 'site') {
      // Site owns `/`. Keep the opened URL; SiteNotFound for unknowns (incl. /scan).
      this.props.dispatchContextualHome('/');
      redirectToPathname = '';
    } else if (surface === 'app') {
      this.props.dispatchContextualHome(`/${rewardRoutes.rewards}`);
      redirectToPathname = `/${rewardRoutes.rewards}`;
    } else {
      // Legacy surface — welcome / engage / table-QR home
      this.props.dispatchContextualHome(`/${routes.containers.welcome}/${defaultScanCode}`);
      redirectToPathname = `/${routes.containers.welcome}/${defaultScanCode}`;
    }

    // Used by app/legacy surfaces only. Site no longer allowlists pages — see branch above.
    const exceptionRoutes = [
      routes.containers.payment,
      routes.containers.confirmation,
      routes.containers.welcome,
      routes.containers.cart,
      routes.containers.offers,
      routes.containers.account,
      routes.containers.aboutOutlet,
      routes.containers.getApp,
      rewardRoutes.rewards,
      orderingRoutes.ordering,
      pagesRoutes.pages,
      routes.containers.redirect,
      routes.containers.view,
    ];

    const currentPathname = window.location.pathname || '/';
    const alreadyOnRedirectTarget =
      !!redirectToPathname &&
      (currentPathname === redirectToPathname ||
        (redirectToPathname === '/' && (currentPathname === '/' || currentPathname === '')));
    const isExceptionPath = exceptionRoutes.some(
      (route) => currentPathname.indexOf(route) > -1,
    );

    if (allowFullPageReplace && redirectToPathname && !alreadyOnRedirectTarget && !isExceptionPath) {
      window.location.replace(window.location.origin + redirectToPathname);
    } else if (
      !allowFullPageReplace &&
      redirectToPathname &&
      !alreadyOnRedirectTarget &&
      !isExceptionPath
    ) {
      // Boot hydrate: SPA replace for entry paths not on this surface's route set.
      history.replace(redirectToPathname);
    }

    this.enablePWAInstallWidgetsOnIOS();
  };

  getGuestCashCards = (guestId) => {
    const pageSize = cashCardAPIParams.guestCashCards.pageSize;
    const sortType = cashCardAPIParams.guestCashCards.sortType;

    const constructedURL = `/cashCards/guests?guestId=${guestId}&size=${pageSize}&sort=${sortType}`;

    this.props.fetchGuestCashCards({ url: constructedURL });
  };

  handleInstallPWAClick = () => {
    const promptEvent = this.state.deferredPrompt;
    if (!promptEvent) {
      // The deferred prompt isn't available
      return;
    }
    // Show the PWA install prompt
    promptEvent.prompt();
    // Detect if the user chose to install PWA
    promptEvent.userChoice.then((result) => {
      const pwaInstallUserChoice = get(result, 'outcome', '');
      this.setState({ pwaInstallUserChoice });

      if (pwaInstallUserChoice === pwaInstallUserChoices.accepted) {
        // Reset the deferred prompt variable, since prompt() can only be called once
        this.setState({ deferredPrompt: null });
        this.setPWAInstalledState(true);
        this.hidePWAInstallWidgets();
      }
    });
  };

  hidePWAInstallWidgets = () => {
    this.setState({
      enablePWAInstallWidgets: false,
    });
  };

  setPWAInstalledState = (isPWAInstalled) => {
    this.setState({
      isPWAInstalled,
    });
  };

  enablePWAInstallWidgetsOnIOS = () => {
    deviceType === 'ios' &&
      this.setState({
        isPWAInstallable: true, // PWA is immediately available to install on iOS Safari on webapp load
        enablePWAInstallWidgets: true,
      });
  };

  // To promote PWA discovery, in case of non-whitelabel visit to the webapp, enable PWA install widgets if the restaurant's get-app link exists
  enablePWAWidgetsForDiscovery = () => {
    const subdomain = extractWhitelabelSubdomain();
    !subdomain &&
      this.setState({
        enablePWAInstallWidgets: !isEmpty(this.props.restaurantAppLink) ? true : false,
      });
  };

  clearSessionAndRedirect = () => {
    const { persistor } = rootStore;
    // Clear the store being persisted from session storage
    persistor
      .purge()
      .then(() => {
        this.props.markPersistorForPurgeAction(false).then(() => {
          window.location.replace(window.location.origin);
        });
      })
      .catch((error) => {
        console.error('There was an error purging the store', error);
      });
  };

  handleRedirectToOrderingSiteClick = () => {
    this.setState(
      {
        showDesktopLandingScreen: false,
      },
      () => {
        this.props.dispatchUserConsentAction({
          redirectToOrderingSite: true,
        });
      },
    );
  };

  resolveScanRouteComponent = ({
    props,
    showDesktopLandingScreen,
    redirectToOrderingSite,
    showCashCardFeatureOnScanScreenFlag,
  }) => {
    let component = '';
    if (showDesktopLandingScreen && !redirectToOrderingSite) {
      component = (
        <DesktopLandingScreen
          handleRedirectToOrderingSiteClick={this.handleRedirectToOrderingSiteClick}
        />
      );
    } else {
      component = <Scan {...props} showCashCardFeature={showCashCardFeatureOnScanScreenFlag} />;
    }
    return component;
  };

  handleLogout = () => {
    const { dispatchLogoutAction } = this.props;
    localStorage.setItem('GuestLoginData', JSON.stringify({}));
    localStorage.setItem('AuthData', JSON.stringify({}));
    dispatchLogoutAction();
    this.handleLogoutRedirection();
  };

  handleLogoutRedirection = () => {
    const { selectedTable, restaurant } = this.props;
    const selectedTableScanCode = get(selectedTable, 'scanCode', '');
    const selectedRestaurantCode = get(restaurant, 'restaurantCode', '');
    if (!isEmpty(selectedTableScanCode)) {
      const pathname = `/${routes.containers.restaurant}/${selectedTableScanCode}`;
      // Clear any prior flag first so Restaurant sees absent → true on location.state
      history.replace({ pathname, state: undefined });
      history.push({
        pathname,
        state: { showAuthenticationPopup: true },
      });
    } else if (!isEmpty(selectedRestaurantCode)) {
      history.push(`/${routes.containers.welcome}/${selectedRestaurantCode}`);
    }
  };

  handleSessionExpiredErrorResponse = (errorResponse) => {
    const { clearApiErrorResponse } = this.props;
    if (notifyNativeSessionExpired()) {
      clearApiErrorResponse();
      return;
    }
    setIsApiRequestOnHold(false);
    const isAppInStartupPhase = getAppInStartupPhase();
    if (isAppInStartupPhase) {
      this.handleLogout(true);
      clearApiErrorResponse();
    } else {
      this.setState({ showSessionExpired: true });
    }
  };

  handleCloseSessionExpired = () => {
    const { clearApiErrorResponse } = this.props;
    const redirectToLoginScreen = true;
    this.setState({ showSessionExpired: false }, () => {
      clearApiErrorResponse();
      this.handleLogout(redirectToLoginScreen);
    });
  };

  getPremiumFeaturesEnabledForRestaurant = (restaurantConfigAdditionalProps) => {
    const restaurantPremiumFeaturesObject =
      restaurantConfigAdditionalProps.find((prop) => get(prop, 'code') === 'plan.plus.features') ||
      {};
    const restaurantPremiumFeaturesList = get(restaurantPremiumFeaturesObject, 'value', '').split(
      ',',
    ); // Get comma separated feature keys and return them in an array

    const restaurantSubscribedToPWAFeature = restaurantPremiumFeaturesList.includes(
      restaurantPremiumFeaturesKeys.pwa,
    );
    const restaurantSubscribedToUpsellFeature = restaurantPremiumFeaturesList.includes(
      restaurantPremiumFeaturesKeys.upsell,
    );
    const restaurantSubscribedToCashCardFeature = restaurantPremiumFeaturesList.includes(
      restaurantPremiumFeaturesKeys.cashCard,
    );

    return {
      restaurantSubscribedToPWAFeature,
      restaurantSubscribedToUpsellFeature,
      restaurantSubscribedToCashCardFeature,
    };
  };

  handleGoHome = () => {
    window.location.replace(window.location.origin);
  };

  componentWillUnmount() {
    this.unlistenHistory && this.unlistenHistory();
    this.props.clearProgressiveWebAppData();
  }

  render() {
    const {
      redirectToOrderingSite,
      showSpinner,
      contextualHome,
      restaurantConfigAdditionalProps,
      guestCashCards,
      surface,
    } = this.props;
    const {
      isRestaurantEntityGroup,
      enablePWAInstallWidgets,
      showDesktopLandingScreen,
      isPWAInstalled,
      pwaInstallUserChoice,
      isPWAInstallable,
      isScreenOrientationPortrait,
      showNonExistentSubdomainFallback,
      showSessionExpired,
      locationPathname,
    } = this.state;

    let restaurantSubscribedToUpsellFeature = true,
      restaurantSubscribedToCashCardFeature = true;

    if (!subscriptionModuleEnabled) {
      // Restaurant-specific premium feature subscription flags
      restaurantSubscribedToUpsellFeature = get(
        this.getPremiumFeaturesEnabledForRestaurant(restaurantConfigAdditionalProps),
        'restaurantSubscribedToUpsellFeature',
      );
      restaurantSubscribedToCashCardFeature = get(
        this.getPremiumFeaturesEnabledForRestaurant(restaurantConfigAdditionalProps),
        'restaurantSubscribedToCashCardFeature',
      );
    }

    // PWA feature related flags
    const showPWAFeatureFlag = pwaFeatureEnabled;

    // Upsell feature related flags
    const showUpsellFeatureFlag = upsellFeatureEnabled && restaurantSubscribedToUpsellFeature;

    // Cash-card feature related flags
    const showCashCardFeatureFlag = cashCardFeatureEnabled && restaurantSubscribedToCashCardFeature;
    const hasGuestAvailedCashCards = !isEmpty(guestCashCards);
    const showCashCardFeatureOnScanScreenFlag = hasGuestAvailedCashCards;

    const levelOneSubPath = locationPathname ? locationPathname.split('/')[1] : '';
    const isRouteEngageScoped = engageRoutes.includes(levelOneSubPath);
    const isPageContext = isEqual(pagesRoutes.pages, levelOneSubPath);
    const isSiteContext = surface === 'site';

    return (
      <DeviceContext.Provider value={deviceType}>
        <SubscriptionContext.Provider
          value={{
            pwa: showPWAFeatureFlag,
            upsell: showUpsellFeatureFlag,
            cashCard: showCashCardFeatureFlag,
          }}
        >
          {!isNativeWebView && isMobilePlatform() && !isSiteContext && (
            <div
              className={classNames('landscape-not-supported-guide', {
                visible: !isScreenOrientationPortrait,
                ios: isIOS(),
              })}
            >
              <img
                className="landscape-placeholder-image"
                src={landscapeImage}
                alt="landscape not supported"
              />
            </div>
          )}
          <div
            className={classNames('container', {
              engage: isRouteEngageScoped,
              page: isPageContext,
              site: isSiteContext,
            })}
          >
            {showSpinner && !showDesktopLandingScreen && <Spinner />}
            {showNonExistentSubdomainFallback ? (
              // handle GoHome should redirect to the base
              <NonExistentSubdomainFallback
                resources={{
                  title: 'Unable to reach this page',
                  description: `It seems some error has occured.
     Please try again`,
                  atnBtn: 'TRY AGAIN',
                }}
                handleGoHome={this.handleGoHome}
              />
            ) : !surface ? (
              // FUDR-14577: wait for resolveSurface before exclusive route mount
              <Spinner />
            ) : (
              <Router history={history}>
                {/* key=surface remounts matched shell on hops (e.g. app → ordering after scan). */}
                <Switch
                  key={
                    isNativeWebView && surface === FUDR_SURFACES.SITE
                      ? FUDR_SURFACES.APP
                      : surface
                  }
                >
                  {renderRoutesForSurface(
                    isNativeWebView && surface === FUDR_SURFACES.SITE
                      ? FUDR_SURFACES.APP
                      : surface,
                    {
                    Pages,
                    Payment,
                    URLRedirectHandler,
                    CustomLinkRedirect,
                    Ordering,
                    Site,
                    RewardsScan,
                    SiteNotFound,
                    Rewards,
                    Wallet,
                    DefaultPage,
                    Restaurant,
                    Cart,
                    Confirmation,
                    OrderList,
                    Account,
                    Offers,
                    AboutFudr,
                    AboutOutlet,
                    TermsAndConditions,
                    PrivacyPolicy,
                    HelpAndSupport,
                    CashCards,
                    Deals,
                    CouponDetail,
                    DealDetail,
                    Feedback,
                    RewardsClubSignup,
                    View,
                    WelcomeScreen,
                    DownloadApp,
                    contextualHome,
                    showPWAFeatureFlag,
                    enablePWAInstallWidgets,
                    handleInstallPWAClick: this.handleInstallPWAClick,
                    isPWAInstalled,
                    pwaInstallUserChoice,
                    isPWAInstallable,
                    showDesktopLandingScreen,
                    redirectToOrderingSite,
                    showCashCardFeatureOnScanScreenFlag,
                    showCashCardFeatureFlag,
                    isRestaurantEntityGroup,
                    resolveScanRouteComponent: this.resolveScanRouteComponent,
                    renderGreetingSplashScreenComponent: this.renderGreetingSplashScreenComponent,
                  })}
                </Switch>
              </Router>
            )}
            {/* Load any resources needed to be cached in case user agent goes offline */}
            <span style={{ display: 'none' }}></span>
            <img src={exceptionEmoji} style={{ display: 'none' }} alt="exception" />
            <img src={cancelIcon} style={{ display: 'none' }} alt="cancel-icon" />
          </div>
          {showSessionExpired && (
            <SessionExpiredPopover
              open={showSessionExpired}
              onCTAClick={this.handleCloseSessionExpired}
            />
          )}
        </SubscriptionContext.Provider>
      </DeviceContext.Provider>
    );
  }
}

export default connect(
  (state) => ({
    loginData: get(state, 'authentication.loginData', {}),
    persistorMarkedForPurge: get(state, 'app.persistorMarkedForPurge', false),
    redirectToOrderingSite: get(state, 'app.userConsent.redirectToOrderingSite', false),
    showSpinner: get(state, 'app.showSpinner', false),
    contextualHome: get(state, 'common.contextualHome', ''),
    surface: get(state, 'common.surface', ''),
    modules: get(state, 'common.modules', []),
    appStartupInfo: get(state, 'common.appStartupInfo.subdomainObj', {}),
    restaurantConfigAdditionalProps: get(
      state,
      'app.restaurant.restaurantConfig.additionalProperties',
      [],
    ),
    guestCashCards: get(state, 'app.guestCashCards._embedded.guestCashCards', []),
    restaurantAppLink: get(state, 'app.progressiveWebApp.appLink', ''),
    restaurantGroup: get(state, 'app.restaurantGroup', {}),
    restaurant: get(state, 'app.restaurant', {}),
    guestData: get(state, 'authentication.guestData', {}),
    masterData: get(state, 'common.masterData', {}),
    errorResponse: get(state, 'common.errorResponse', {}),
    selectedTable: get(state, 'app.selectedTable', {}),
  }),
  {
    fetchAppStartupInfo: fetchAppStartupInfoAction,
    hydrateAppStartupFromBoot: hydrateAppStartupFromBootAction,
    markPersistorForPurgeAction,
    dispatchUserConsentAction,
    fetchMasterDataAction,
    getGuestByIdAction,
    dispatchContextualHome,
    dispatchSurface,
    fetchRestaurantDataAction,
    fetchGuestCashCards: fetchGuestCashCardsAction,
    clearProgressiveWebAppData: clearProgressiveWebAppDataAction,
    dispatchApplicableBrandModules: dispatchApplicableBrandModulesAction,
    clearApiErrorResponse: clearApiErrorResponseAction,
    dispatchLogoutAction,
  },
)(App);
